import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Container,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import api from '../config/api';
import { toast } from 'react-toastify';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import { LoadingButton } from '@mui/lab';

function Configuracoes() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sslLoading, setSslLoading] = useState(false);
  const [proxyLoading, setProxyLoading] = useState(false);
  const [config, setConfig] = useState({
    token_admin: '',
    senha_atual: '',
    nova_senha: '',
    confirmar_senha: '',
    dominio: '',
    proxy: {
      frontendPort: 3002,
      backendPort: 3001,
      httpPort: 80,
      httpsPort: 443
    }
  });
  const [sslInfo, setSslInfo] = useState({
    instalado: false,
    valido: false,
    expira_em: null,
    data_expiracao: null,
    dias_restantes: null
  });
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      // Buscar token do administrador
      const tokenResponse = await api.get('/admin/config');
      
      // Buscar configurações do sistema
      const sistemaResponse = await api.get('/admin/sistema/configuracoes');
      
      // Buscar configurações de proxy
      const proxyResponse = await api.get('/admin/sistema/configuracoes/proxy');
      
      // Atualizar estado com as configurações obtidas
      setConfig(prev => ({
        ...prev,
        token_admin: tokenResponse.data.token_admin || '',
        dominio: sistemaResponse.data.dominio || '',
        proxy: proxyResponse.data || {
          frontendPort: 3002,
          backendPort: 3001,
          httpPort: 80,
          httpsPort: 443
        }
      }));
      
      // Atualizar informações do SSL
      setSslInfo(sistemaResponse.data.ssl || {
        instalado: false,
        valido: false,
        expira_em: null,
        data_expiracao: null,
        dias_restantes: null
      });
      
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarToken = () => {
    setConfig((prev) => ({
      ...prev,
      token_admin: uuidv4()
    }));
  };

  const handleSalvarToken = async () => {
    setSaving(true);
    try {
      await api.put('/admin/config/token', {
        token_admin: config.token_admin
      });
      toast.success('Token de administração atualizado com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar token:', error);
      toast.error('Erro ao atualizar token de administração');
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarDominio = async () => {
    setSaving(true);
    try {
      await api.put('/admin/sistema/configuracoes/dominio', {
        dominio: config.dominio
      });
      toast.success('Domínio atualizado com sucesso');
      
      // Atualizar configurações
      fetchConfig();
    } catch (error) {
      console.error('Erro ao atualizar domínio:', error);
      
      if (error.response && error.response.data && error.response.data.erro) {
        toast.error(error.response.data.erro);
      } else {
        toast.error('Erro ao atualizar domínio');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSolicitarCertificado = async () => {
    setSslLoading(true);
    try {
      await api.post('/admin/sistema/configuracoes/ssl/solicitar');
      toast.success('Certificado SSL solicitado com sucesso');
      
      // Atualizar configurações para mostrar o novo status do SSL
      fetchConfig();
    } catch (error) {
      console.error('Erro ao solicitar certificado SSL:', error);
      
      if (error.response && error.response.data && error.response.data.erro) {
        toast.error(error.response.data.erro);
        
        if (error.response.data.mensagem) {
          toast.info(error.response.data.mensagem);
        }
      } else {
        toast.error('Erro ao solicitar certificado SSL');
      }
    } finally {
      setSslLoading(false);
    }
  };

  const handleAlterarSenha = async () => {
    if (config.nova_senha !== config.confirmar_senha) {
      toast.error('As senhas não coincidem');
      return;
    }

    setSaving(true);
    try {
      await api.put('/admin/config/senha', {
        senha_atual: config.senha_atual,
        nova_senha: config.nova_senha
      });
      toast.success('Senha alterada com sucesso');
      setOpenDialog(false);
      setConfig((prev) => ({
        ...prev,
        senha_atual: '',
        nova_senha: '',
        confirmar_senha: ''
      }));
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Erro ao alterar senha');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarProxy = async () => {
    setProxyLoading(true);
    try {
      await api.put('/admin/sistema/configuracoes/proxy', {
        frontendPort: config.proxy.frontendPort,
        backendPort: config.proxy.backendPort,
        httpPort: config.proxy.httpPort,
        httpsPort: config.proxy.httpsPort
      });
      toast.success('Configurações de proxy atualizadas com sucesso');
      
      // Atualizar configurações
      fetchConfig();
    } catch (error) {
      console.error('Erro ao atualizar configurações de proxy:', error);
      
      if (error.response && error.response.data && error.response.data.erro) {
        toast.error(error.response.data.erro);
      } else {
        toast.error('Erro ao atualizar configurações de proxy');
      }
    } finally {
      setProxyLoading(false);
    }
  };

  const handleProxyChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      proxy: {
        ...prev.proxy,
        [name]: parseInt(value) || ''
      }
    }));
  };

  const formatarData = (dataIso) => {
    if (!dataIso) return 'Não disponível';
    
    try {
      const data = new Date(dataIso);
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Data inválida';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Configurações do Sistema
        </Typography>
        
        <Grid container spacing={3}>
          {/* Configuração de Domínio e SSL */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Domínio e SSL
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Configure o domínio de acesso da aplicação e gerencie o certificado SSL
              </Alert>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Domínio
                </Typography>
                <TextField
                  fullWidth
                  label="Domínio da Aplicação"
                  placeholder="exemplo.com"
                  value={config.dominio}
                  onChange={(e) => setConfig({ ...config, dominio: e.target.value })}
                  margin="normal"
                  helperText="O domínio que aponta para este servidor"
                />
                <Button
                  variant="contained"
                  onClick={handleSalvarDominio}
                  disabled={saving}
                  sx={{ mt: 1 }}
                >
                  {saving ? 'Salvando...' : 'Salvar Domínio'}
                </Button>
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  Certificado SSL
                  {sslInfo.valido ? (
                    <CheckCircleIcon color="success" sx={{ ml: 1 }} />
                  ) : (
                    <ErrorIcon color="warning" sx={{ ml: 1 }} />
                  )}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }} component="div">
                    <strong>Status:</strong> &nbsp;
                    <Chip 
                      icon={sslInfo.valido ? <LockIcon /> : <LockOpenIcon />}
                      label={sslInfo.valido ? 'Válido' : (sslInfo.instalado ? 'Expirado' : 'Não Instalado')}
                      color={sslInfo.valido ? 'success' : 'warning'}
                      size="small"
                    />
                  </Typography>
                  
                  {sslInfo.instalado && (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Expira em:</strong> {formatarData(sslInfo.data_expiracao)}
                      </Typography>
                      
                      {sslInfo.dias_restantes !== null && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Dias Restantes:</strong> {sslInfo.dias_restantes} dias
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
                
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleSolicitarCertificado}
                  disabled={sslLoading || !config.dominio}
                >
                  {sslLoading ? 'Processando...' : 'Solicitar/Renovar Certificado SSL'}
                </Button>
                
                {!config.dominio && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    Configure um domínio antes de solicitar um certificado SSL
                  </Typography>
                )}
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Configurações de Proxy
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Configure as portas para redirecionar o tráfego entre frontend e backend
                </Alert>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Porta do Frontend"
                      type="number"
                      name="frontendPort"
                      value={config.proxy.frontendPort}
                      onChange={handleProxyChange}
                      margin="normal"
                      helperText="Porta onde o frontend está rodando"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Porta do Backend"
                      type="number"
                      name="backendPort"
                      value={config.proxy.backendPort}
                      onChange={handleProxyChange}
                      margin="normal"
                      helperText="Porta onde o backend está rodando"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Porta HTTP"
                      type="number"
                      name="httpPort"
                      value={config.proxy.httpPort}
                      onChange={handleProxyChange}
                      margin="normal"
                      helperText="Porta para acesso via HTTP (padrão: 80)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Porta HTTPS"
                      type="number"
                      name="httpsPort"
                      value={config.proxy.httpsPort}
                      onChange={handleProxyChange}
                      margin="normal"
                      helperText="Porta para acesso via HTTPS (padrão: 443)"
                    />
                  </Grid>
                </Grid>
                
                <Button
                  variant="contained"
                  onClick={handleSalvarProxy}
                  disabled={proxyLoading}
                  sx={{ mt: 2 }}
                >
                  {proxyLoading ? 'Salvando...' : 'Salvar Configurações de Proxy'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Token de Administração */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Token de Administração
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Este token é usado para autenticar chamadas à API de administração
              </Alert>
              <TextField
                fullWidth
                label="Token de Administração"
                value={config.token_admin}
                onChange={(e) =>
                  setConfig({ ...config, token_admin: e.target.value })
                }
                margin="normal"
              />
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleGerarToken}
                  disabled={saving}
                >
                  Gerar Novo Token
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSalvarToken}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar Token'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Senha de Administração */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Senha de Administração
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Altere a senha de acesso ao painel administrativo
              </Alert>
              <Button
                variant="contained"
                onClick={() => setOpenDialog(true)}
                disabled={saving}
              >
                Alterar Senha
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Dialog de Alteração de Senha */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Alterar Senha</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Senha Atual"
              type="password"
              fullWidth
              value={config.senha_atual}
              onChange={(e) =>
                setConfig({ ...config, senha_atual: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Nova Senha"
              type="password"
              fullWidth
              value={config.nova_senha}
              onChange={(e) =>
                setConfig({ ...config, nova_senha: e.target.value })
              }
            />
            <TextField
              margin="dense"
              label="Confirmar Nova Senha"
              type="password"
              fullWidth
              value={config.confirmar_senha}
              onChange={(e) =>
                setConfig({ ...config, confirmar_senha: e.target.value })
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button
              onClick={handleAlterarSenha}
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default Configuracoes; 